import React, { useEffect, useState } from 'react';
import { superAdminApi } from '@/services/superAdminApi';
import { CreditCard, Building2, Landmark, DollarSign, Loader2, CheckCircle2, CircleDashed, ShieldAlert, Store } from 'lucide-react';
import { toast } from 'sonner';

const chf = (n) => 'CHF ' + (Number(n) || 0).toLocaleString('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const Badge = ({ ok, yes = 'Verbunden', no = 'Nicht verbunden' }) => (
  ok
    ? <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-xs text-emerald-700"><CheckCircle2 className="h-3 w-3" /> {yes}</span>
    : <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600"><CircleDashed className="h-3 w-3" /> {no}</span>
);

export default function PaymentsPage() {
  const [tab, setTab] = useState('invictax');
  const [inv, setInv] = useState(null);
  const [man, setMan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([superAdminApi.payInvictax(), superAdminApi.payMandanten()]);
      setInv(a.data);
      setMan(b.data);
    } catch (e) {
      toast.error('Zahlungsdaten konnten nicht geladen werden');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const changeProvider = async (slug, provider) => {
    setSaving(slug);
    try {
      await superAdminApi.paySetTenantProvider(slug, provider);
      toast.success(`Anbieter für ${slug} auf ${provider} gesetzt`);
      setInv((prev) => ({ ...prev, tenants: prev.tenants.map((t) => t.slug === slug ? { ...t, provider } : t) }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally { setSaving(''); }
  };

  const providerStatusChip = (p) => (
    p.status === 'active'
      ? <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[11px] text-emerald-700"><CheckCircle2 className="h-3 w-3" /> Aktiv</span>
      : <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/15 px-2 py-0.5 text-[11px] text-amber-700"><CircleDashed className="h-3 w-3" /> Vorbereitet</span>
  );

  if (loading) return <div className="text-slate-600" data-testid="sa-payments-loading">Lädt…</div>;

  return (
    <div data-testid="sa-payments" className="space-y-8">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Zahlungen</h1>
        <p className="mt-1 text-sm text-slate-600">Zwei strikt getrennte Geldflüsse. Links fliesst Geld an InvictaX, rechts an die Mandanten.</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 rounded-xl border border-slate-200 bg-white p-1">
        <button
          onClick={() => setTab('invictax')}
          data-testid="sa-payments-tab-invictax"
          className={`flex flex-1 items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium transition-colors ${tab === 'invictax' ? 'bg-indigo-500/20 text-indigo-700' : 'text-slate-600 hover:text-slate-900'}`}
        >
          <Landmark className="h-4 w-4" /> InvictaX Payments
        </button>
        <button
          onClick={() => setTab('mandanten')}
          data-testid="sa-payments-tab-mandanten"
          className={`flex flex-1 items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium transition-colors ${tab === 'mandanten' ? 'bg-indigo-500/20 text-indigo-700' : 'text-slate-600 hover:text-slate-900'}`}
        >
          <Store className="h-4 w-4" /> Mandanten Payments
        </button>
      </div>

      {tab === 'invictax' && inv && (
        <div className="space-y-6" data-testid="sa-payments-invictax">
          <div className="flex items-start gap-3 rounded-xl border border-indigo-500/20 bg-indigo-500/5 p-4 text-sm text-indigo-700">
            <Landmark className="mt-0.5 h-4 w-4 shrink-0" />
            <span>Einnahmen der <strong>InvictaX Group GmbH</strong> für KI-Guthaben &amp; Software. Mandant → InvictaX.</span>
          </div>

          {/* Totals + available providers */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="rounded-2xl border border-slate-200 bg-white p-6">
              <div className="mb-3 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-green-500/15 text-green-700"><DollarSign className="h-5 w-5" /></div>
              <div className="text-3xl font-semibold text-slate-900">{chf(inv.revenue.total_chf)}</div>
              <div className="text-sm text-slate-600">Umsatz KI-Guthaben</div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-6">
              <div className="mb-3 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-indigo-500/15 text-indigo-700"><CreditCard className="h-5 w-5" /></div>
              <div className="text-3xl font-semibold text-slate-900">{inv.revenue.paid_count}</div>
              <div className="text-sm text-slate-600">Bezahlte Käufe</div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-6">
              <div className="mb-2 text-sm font-medium text-slate-700">Verfügbare Anbieter</div>
              <div className="space-y-2">
                {inv.providers.map((p) => (
                  <div key={p.key} className="flex items-center justify-between" data-testid={`sa-payments-provider-${p.key}`}>
                    <div><span className="text-sm text-slate-800">{p.label}</span><div className="text-[11px] text-slate-500">{p.note}</div></div>
                    {providerStatusChip(p)}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Per-tenant provider selection */}
          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <h2 className="mb-1 text-lg font-medium text-slate-900">Aktiver Anbieter je Mandant</h2>
            <p className="mb-4 text-xs text-slate-500">Pro Mandant wählbar, über welchen Anbieter die KI-Guthaben-Zahlung an InvictaX läuft.</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                    <th className="pb-3 font-medium">Mandant</th>
                    <th className="pb-3 font-medium">Anbieter</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {inv.tenants.map((t) => (
                    <tr key={t.slug} data-testid={`sa-payments-tenant-${t.slug}`}>
                      <td className="py-3 text-slate-800">{t.name || t.slug}<div className="text-xs text-slate-500">{t.slug}</div></td>
                      <td className="py-3">
                        <div className="flex items-center gap-2">
                          <select
                            className="rounded-lg border border-slate-200 bg-slate-100 px-2 py-1 text-slate-800"
                            value={t.provider}
                            disabled={saving === t.slug}
                            onChange={(e) => changeProvider(t.slug, e.target.value)}
                            data-testid={`sa-payments-provider-select-${t.slug}`}
                          >
                            {inv.providers.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
                          </select>
                          {saving === t.slug && <Loader2 className="h-4 w-4 animate-spin text-slate-600" />}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Revenue log */}
          <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="sa-payments-revenue">
            <h2 className="mb-4 text-lg font-medium text-slate-900">Umsatz-Log</h2>
            {(!inv.revenue.transactions || inv.revenue.transactions.length === 0) ? (
              <p className="text-sm text-slate-500">Noch keine Zahlungen.</p>
            ) : (
              <table className="w-full text-sm">
                <thead><tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500"><th className="pb-3">Datum</th><th className="pb-3">Mandant</th><th className="pb-3">Typ</th><th className="pb-3 text-right">Betrag</th></tr></thead>
                <tbody className="divide-y divide-slate-200">
                  {inv.revenue.transactions.map((r, i) => (
                    <tr key={r.session_id || i}>
                      <td className="py-2 text-slate-600">{(r.created_at || '').slice(0, 16).replace('T', ' ')}</td>
                      <td className="py-2 text-slate-700">{r.tenant_slug}</td>
                      <td className="py-2 text-slate-600">{r.type}</td>
                      <td className="py-2 text-right text-green-700">{chf(r.amount_chf)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {tab === 'mandanten' && man && (
        <div className="space-y-6" data-testid="sa-payments-mandanten">
          <div className="flex items-start gap-3 rounded-xl border border-amber-500/20 bg-amber-500/5 p-4 text-sm text-amber-700">
            <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
            <span>Anbieter, die Mandanten für ihre <strong>eigenen Endkunden</strong> nutzen. Dieses Geld geht an den jeweiligen Mandanten, <strong>nicht</strong> an InvictaX. Nur-Lese-Übersicht – keine Zugangsdaten sichtbar.</span>
          </div>

          <div className="space-y-4">
            {man.tenants.map((t) => (
              <div key={t.slug} className="rounded-2xl border border-slate-200 bg-white p-6" data-testid={`sa-payments-merchant-${t.slug}`}>
                <div className="mb-4 flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-slate-600" />
                  <div className="text-base font-medium text-slate-900">{t.name || t.slug}</div>
                  <span className="text-xs text-slate-500">{t.slug}</span>
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                    <div className="mb-2 flex items-center justify-between"><span className="text-sm font-medium text-slate-800">Payrexx</span><Badge ok={t.payrexx.default_configured} /></div>
                    <div className="text-xs text-slate-500">{t.payrexx.default_instance ? `Instanz: ${t.payrexx.default_instance}` : 'Keine Standard-Instanz'}</div>
                    <div className="text-xs text-slate-500">Studio-Overrides: {t.payrexx.studio_overrides}</div>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                    <div className="mb-2 flex items-center justify-between"><span className="text-sm font-medium text-slate-800">SumUp</span><Badge ok={t.sumup.enabled && t.sumup.configured} yes={t.sumup.sandbox ? 'Sandbox' : 'Aktiv'} /></div>
                    <div className="text-xs text-slate-500">{t.sumup.configured ? 'Schlüssel hinterlegt' : 'Kein Schlüssel'}</div>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                    <div className="mb-2 flex items-center justify-between"><span className="text-sm font-medium text-slate-800">Worldline</span><Badge ok={t.worldline.enabled} yes="Aktiv" /></div>
                    <div className="text-xs text-slate-500">Terminal-Zahlung (SUNMI)</div>
                  </div>
                </div>
                {t.studios && t.studios.length > 0 && (
                  <div className="mt-4 text-xs text-slate-500">
                    {t.studios.length} Studio(s) · {t.studios.filter((s) => s.payrexx_override).length} mit eigenem Payrexx-Konto
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
